import { archiveMerchant } from 'config/requests/merchants';
import { useStagedDelete } from 'hooks/useStagedDelete';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { queryClient } from 'providers/queryClient';
import { useNotify, useRedirect } from 'react-admin';
import { Box } from 'shared/mui/Box';
import { CardHeader } from 'shared/mui/Card';
import { Dialog } from 'shared/mui/Dialogs';
import { DeleteStageDialogDenyReason } from 'shared/mui/Dialogs/DeleteStageDialog/DeleteStageDialogDenyReason';
import { DeleteStageDialogForm } from 'shared/mui/Dialogs/DeleteStageDialog/DeleteStageDialogForm';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { Typography, TypographyWithBold } from 'shared/mui/Typography';
import { TypographyBold } from 'shared/mui/Typography/TypographyBold';
import { Merchant } from 'types/embedded/merchant';

type MerchantsArchiveDialogProps = {
  record?: Merchant;
  open: boolean;
  onClose: VoidFunction;
  children: React.ReactNode;
};

export const MerchantsArchiveDialog: React.FC<MerchantsArchiveDialogProps> = ({
  record,
  open,
  onClose,
  children,
}) => {
  const translate = useTypedTranslate();

  const redirect = useRedirect();
  const notify = useNotify();
  const { deleteOneRecord, denyDeleteReason, stage } = useStagedDelete({
    actionFn: (id: string) => archiveMerchant(id, true),
    record,
    onSuccess: () => {
      onClose();
      redirect('list', 'merchants');
      queryClient.refetchQueries({ queryKey: ['merchants'] });
      notify(
        <TypographyWithBold
          text={translate('app.merchants.merchantArchived', { name: record?.name || '' })}
        />,
        { type: 'success' },
      );
    },
  });

  return (
    <Dialog onClose={onClose} open={open} width="640px">
      <Box
        sx={{
          display: 'flex',
          top: '50%',
          flexDirection: 'column',
          padding: 0,
        }}
      >
        <CardHeader
          appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
          title="Архивирование мерчанта"
          titleSize="smallBold"
        />
        <DeleteStageDialogForm
          deleteLabel={translate('app.buttons.inArchive')}
          deleteOneRecord={deleteOneRecord}
          details={
            <Typography>
              Мерчант <TypographyBold fontSize={16} text={record?.name || ''} /> будет переведен в
              архив и перестанет быть доступен для настроек в системе и формирования финансовой
              отчётности. При необходимости, функциональность данного мерчанта может быть
              восстановлена.
            </Typography>
          }
          onClose={onClose}
          open={true}
          stage={stage}
        >
          <DeleteStageDialogDenyReason reason={denyDeleteReason}>
            {children}
          </DeleteStageDialogDenyReason>
        </DeleteStageDialogForm>
      </Box>
    </Dialog>
  );
};
